import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import PageHeader from '../components/PageHeader';
import JobCodeSection from '../components/JobCodeSection';
import Modal from '../components/Modal';
import TaskAssigneePicker from '../components/TaskAssigneePicker';
import { useAuth } from '../context/AuthContext';
import {
  fetchSiteInspection,
  updateSiteInspection,
  completeSiteInspection,
  voidSiteInspection,
  fetchSiteInspectionAttachments,
  uploadSiteInspectionAttachment,
  voidSiteInspectionAttachment,
  unvoidSiteInspectionAttachment,
  siteInspectionAttachmentDownloadUrl,
  type SiteInspectionRow,
  type SiteInspectionAttachmentRow,
  type SiteInspectionFormState,
} from '../api/siteInspectionAppointments';
import AttachmentVoidModal from '../components/AttachmentVoidModal';
import { AttachmentVoidReason } from '../components/AttachmentVoidNote';
import { useAttachmentVoid } from '../hooks/useAttachmentVoid';
import { isVoided } from '../api/attachmentVoid';
import { createInstallationAppointment } from '../api/installationAppointments';
import { fetchVerifiedUsers, type VerifiedUser } from '../api/users';
import {
  SITE_INSPECTION_SECTIONS,
  type SiteInspectionField,
} from '../data/siteInspectionForm';
import './Page.css';
import './Elevators.css';
import './Installation.css';

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const SiteInspectionDetail: React.FC = () => {
  const { id: idParam } = useParams<{ id: string }>();
  const id = Number(idParam);
  const navigate = useNavigate();
  const { user } = useAuth();

  const [appt, setAppt] = useState<SiteInspectionRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [formState, setFormState] = useState<SiteInspectionFormState>({});
  const [attachments, setAttachments] = useState<SiteInspectionAttachmentRow[]>([]);

  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [actionBusy, setActionBusy] = useState(false);

  // Phase 3: schedule installation prompt after completion.
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [installAt, setInstallAt] = useState('');
  const [installEndAt, setInstallEndAt] = useState('');
  const [installMultiDay, setInstallMultiDay] = useState(false);
  const [installAssignees, setInstallAssignees] = useState<number[]>([]);
  const [verifiedUsers, setVerifiedUsers] = useState<VerifiedUser[]>([]);
  const [scheduleBusy, setScheduleBusy] = useState(false);
  const [scheduleError, setScheduleError] = useState('');

  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const readOnly = appt != null && appt.status !== 'Scheduled';

  const load = useCallback(async () => {
    if (!Number.isInteger(id) || id <= 0) {
      setLoadError('Invalid inspection id.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError('');
    try {
      const [data, atts] = await Promise.all([
        fetchSiteInspection(id),
        fetchSiteInspectionAttachments(id).catch(() => []),
      ]);
      setAppt(data);
      setFormState(data.form_state || {});
      setAttachments(atts);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load inspection.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    fetchVerifiedUsers()
      .then((list) => !cancelled && setVerifiedUsers(list))
      .catch(() => !cancelled && setVerifiedUsers([]));
    return () => {
      cancelled = true;
    };
  }, []);

  const assigneePickerUsers = useMemo(() => {
    const hasRole = (u: VerifiedUser) => (u.roles ?? []).includes('installation');
    return [...verifiedUsers]
      .sort((a, b) => {
        const ai = hasRole(a) ? 0 : 1;
        const bi = hasRole(b) ? 0 : 1;
        if (ai !== bi) return ai - bi;
        return a.username.localeCompare(b.username);
      })
      .map((u) => ({ id: u.id, username: u.username }));
  }, [verifiedUsers]);

  const scheduleSave = useCallback(
    (next: SiteInspectionFormState) => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        void updateSiteInspection(id, { form_state: next }).catch(() => {
          /* best-effort autosave */
        });
      }, 700);
    },
    [id]
  );

  const setField = useCallback(
    (key: string, value: unknown) => {
      setFormState((prev) => {
        const next = { ...prev, [key]: value };
        scheduleSave(next);
        return next;
      });
    },
    [scheduleSave]
  );

  const handleComplete = async () => {
    if (!appt) return;
    setActionBusy(true);
    try {
      // Flush any pending autosave first.
      if (saveTimer.current) clearTimeout(saveTimer.current);
      await updateSiteInspection(id, { form_state: formState });
      const updated = await completeSiteInspection(id);
      setAppt(updated);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to complete inspection.');
    } finally {
      setActionBusy(false);
    }
  };

  // Open the "Schedule installation appointment" modal from the completed inspection.
  const openScheduleModal = () => {
    if (!appt) return;
    setInstallAt('');
    setInstallAssignees(appt.assignees.map((a) => a.user_id));
    setScheduleError('');
    setScheduleOpen(true);
  };

  const handleVoid = async () => {
    const reason = voidReason.trim();
    if (!reason) return;
    setActionBusy(true);
    try {
      const updated = await voidSiteInspection(id, reason);
      setAppt(updated);
      setVoidOpen(false);
      setVoidReason('');
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to void inspection.');
    } finally {
      setActionBusy(false);
    }
  };

  const handleReopen = async () => {
    setActionBusy(true);
    try {
      const updated = await updateSiteInspection(id, { status: 'Scheduled' });
      setAppt(updated);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to reopen inspection.');
    } finally {
      setActionBusy(false);
    }
  };

  const submitSchedule = async () => {
    if (!appt || !user?.id) return;
    if (!installAt) {
      setScheduleError('Date and time of installation is required.');
      return;
    }
    if (installMultiDay && !installEndAt) {
      setScheduleError('Enter when the installation ends, or switch back to a single day.');
      return;
    }
    if (installAssignees.length === 0) {
      setScheduleError('Select at least one installer.');
      return;
    }
    setScheduleBusy(true);
    setScheduleError('');
    try {
      const created = await createInstallationAppointment({
        user_id: user.id,
        client_id: appt.client_id,
        business_name: appt.business_name,
        business_address: appt.business_address,
        point_of_contact: appt.point_of_contact,
        poc_email: appt.poc_email,
        poc_phone: appt.poc_phone,
        installation_at: new Date(installAt).toISOString(),
        installation_end_at: installMultiDay ? new Date(installEndAt).toISOString() : null,
        assignee_user_ids: installAssignees,
      });
      await updateSiteInspection(id, { installation_appointment_id: created.id });
      setScheduleOpen(false);
      navigate(`/installation/${created.id}`);
    } catch (e) {
      setScheduleError(e instanceof Error ? e.message : 'Failed to schedule installation.');
    } finally {
      setScheduleBusy(false);
    }
  };

  const onUploadPhoto = async (fieldId: string, file: File) => {
    try {
      const row = await uploadSiteInspectionAttachment(id, fieldId, file, user?.id ?? null);
      setAttachments((prev) => [...prev, row]);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to upload photo.');
    }
  };

  const replacePhoto = (row: SiteInspectionAttachmentRow) =>
    setAttachments((prev) => prev.map((a) => (a.id === row.id ? { ...a, ...row } : a)));

  const photoVoid = useAttachmentVoid<SiteInspectionAttachmentRow>({
    noun: 'photo',
    label: (p) => p.original_file_name,
    onVoid: async (p, reason) => {
      replacePhoto(await voidSiteInspectionAttachment(id, p.id, reason, user?.id ?? null));
    },
    onUnvoid: async (p) => {
      replacePhoto(await unvoidSiteInspectionAttachment(id, p.id, user?.id ?? null));
    },
    onError: (message) => alert(message),
  });

  const renderField = (field: SiteInspectionField) => {
    const value = formState[field.id];
    const label = (
      <label className="si-field-label">
        {field.label}
        {field.required ? <span className="si-field-required">*</span> : null}
      </label>
    );

    switch (field.type) {
      case 'text':
        return (
          <div className="si-field" key={field.id}>
            {label}
            <input
              type="text"
              className="form-input"
              value={typeof value === 'string' ? value : ''}
              placeholder={field.placeholder}
              disabled={readOnly}
              onChange={(e) => setField(field.id, e.target.value)}
            />
          </div>
        );
      case 'textarea':
        return (
          <div className="si-field" key={field.id}>
            {label}
            <textarea
              className="form-input"
              rows={4}
              value={typeof value === 'string' ? value : ''}
              placeholder={field.placeholder}
              disabled={readOnly}
              onChange={(e) => setField(field.id, e.target.value)}
            />
          </div>
        );
      case 'date':
        return (
          <div className="si-field" key={field.id}>
            {label}
            <input
              type="date"
              className="form-input"
              value={typeof value === 'string' ? value : ''}
              disabled={readOnly}
              onChange={(e) => setField(field.id, e.target.value)}
            />
          </div>
        );
      case 'radio':
        return (
          <div className="si-field" key={field.id}>
            {label}
            <div className="si-radio-list">
              {(field.options ?? []).map((opt) => (
                <label className="si-radio-row" key={opt}>
                  <input
                    type="radio"
                    name={field.id}
                    checked={value === opt}
                    disabled={readOnly}
                    onChange={() => setField(field.id, opt)}
                  />
                  {opt}
                </label>
              ))}
              {field.hasOther ? (
                <label className="si-radio-row">
                  <input
                    type="radio"
                    name={field.id}
                    checked={typeof value === 'string' && !!value && !(field.options ?? []).includes(value)}
                    disabled={readOnly}
                    onChange={() => setField(field.id, formState[`${field.id}__other`] || 'Other')}
                  />
                  Other:
                  <input
                    type="text"
                    className="form-input"
                    style={{ marginLeft: '0.5rem', maxWidth: 260 }}
                    value={typeof formState[`${field.id}__other`] === 'string' ? (formState[`${field.id}__other`] as string) : ''}
                    disabled={readOnly}
                    onChange={(e) => {
                      setField(`${field.id}__other`, e.target.value);
                      setField(field.id, e.target.value);
                    }}
                  />
                </label>
              ) : null}
            </div>
          </div>
        );
      case 'checkboxes': {
        const arr = Array.isArray(value) ? (value as string[]) : [];
        const toggle = (opt: string) => {
          const next = arr.includes(opt) ? arr.filter((x) => x !== opt) : [...arr, opt];
          setField(field.id, next);
        };
        return (
          <div className="si-field" key={field.id}>
            {label}
            <div className="si-radio-list">
              {(field.options ?? []).map((opt) => (
                <label className="si-radio-row" key={opt}>
                  <input type="checkbox" checked={arr.includes(opt)} disabled={readOnly} onChange={() => toggle(opt)} />
                  {opt}
                </label>
              ))}
              {field.hasOther ? (
                <label className="si-radio-row">
                  Other:
                  <input
                    type="text"
                    className="form-input"
                    style={{ marginLeft: '0.5rem', maxWidth: 260 }}
                    value={typeof formState[`${field.id}__other`] === 'string' ? (formState[`${field.id}__other`] as string) : ''}
                    disabled={readOnly}
                    onChange={(e) => setField(`${field.id}__other`, e.target.value)}
                  />
                </label>
              ) : null}
            </div>
          </div>
        );
      }
      case 'scale': {
        const min = field.scaleMin ?? 1;
        const max = field.scaleMax ?? 5;
        const opts: number[] = [];
        for (let n = min; n <= max; n++) opts.push(n);
        return (
          <div className="si-field" key={field.id}>
            {label}
            <div className="si-scale-row">
              {opts.map((n) => (
                <label className="si-scale-opt" key={n}>
                  {n}
                  <input
                    type="radio"
                    name={field.id}
                    checked={String(value) === String(n)}
                    disabled={readOnly}
                    onChange={() => setField(field.id, String(n))}
                  />
                </label>
              ))}
            </div>
          </div>
        );
      }
      case 'grid': {
        const gridVal = (value && typeof value === 'object' && !Array.isArray(value)
          ? (value as Record<string, string>)
          : {}) as Record<string, string>;
        return (
          <div className="si-field" key={field.id}>
            {label}
            <div className="events-table-wrap">
              <table className="si-grid-table">
                <thead>
                  <tr>
                    <th />
                    {(field.gridColumns ?? []).map((c) => (
                      <th key={c}>{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(field.gridRows ?? []).map((row) => (
                    <tr key={row.id}>
                      <td>{row.label}</td>
                      {(field.gridColumns ?? []).map((c) => (
                        <td key={c}>
                          <input
                            type="radio"
                            name={`${field.id}__${row.id}`}
                            checked={gridVal[row.id] === c}
                            disabled={readOnly}
                            onChange={() => setField(field.id, { ...gridVal, [row.id]: c })}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      }
      case 'file': {
        const fieldPhotos = attachments
          .filter((a) => a.field_id === field.id)
          .sort((a, b) => Number(isVoided(a)) - Number(isVoided(b)));
        return (
          <div className="si-field" key={field.id}>
            {label}
            {!readOnly ? (
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void onUploadPhoto(field.id, f);
                  e.target.value = '';
                }}
              />
            ) : null}
            <div className="si-photo-list">
              {fieldPhotos.map((p) => (
                <div
                  className={`si-photo-item${isVoided(p) ? ' attachment-voided-thumb' : ''}`}
                  key={p.id}
                >
                  <a href={siteInspectionAttachmentDownloadUrl(id, p.id)} target="_blank" rel="noreferrer">
                    <img src={siteInspectionAttachmentDownloadUrl(id, p.id)} alt={p.original_file_name} />
                  </a>
                  {!readOnly ? (
                    <button
                      type="button"
                      className={isVoided(p) ? 'attachment-unvoid-btn' : 'attachment-void-btn'}
                      disabled={photoVoid.busy}
                      onClick={() => (isVoided(p) ? photoVoid.unvoid(p) : photoVoid.requestVoid(p))}
                    >
                      {isVoided(p) ? 'Unvoid' : 'Void'}
                    </button>
                  ) : null}
                  <AttachmentVoidReason attachment={p} />
                </div>
              ))}
              {fieldPhotos.length === 0 && readOnly ? <span className="page-subtitle">No photos uploaded.</span> : null}
            </div>
          </div>
        );
      }
      default:
        return null;
    }
  };

  return (
    <div className="page-container">
      <div className="page-layout">
        <Sidebar />
        <main className="page-main">
          <PageHeader title="Site Inspection" subtitle="Initial Site Inspection Form" />
          <div className="page-content">
            <button type="button" className="cancel-button" onClick={() => navigate('/installation?tab=site-inspection')} style={{ marginBottom: '1rem' }}>
              ← Back to Site Inspections
            </button>

            {loading ? (
              <p className="page-subtitle">Loading inspection…</p>
            ) : loadError ? (
              <p className="create-order-error" role="alert">{loadError}</p>
            ) : appt ? (
              <>
                <div className="si-detail-header">
                  <div>
                    <h2 style={{ margin: '0 0 0.35rem' }}>{appt.business_name}</h2>
                    <span className={`si-status-badge si-status-${appt.status}`}>{appt.status}</span>
                  </div>
                  <div className="si-detail-actions">
                    {appt.status === 'Scheduled' ? (
                      <>
                        <button type="button" className="update-button" disabled={actionBusy} onClick={() => void handleComplete()}>
                          Complete
                        </button>
                        <button type="button" className="cancel-button" disabled={actionBusy} onClick={() => setVoidOpen(true)}>
                          Void
                        </button>
                      </>
                    ) : (
                      <>
                        {appt.status === 'Completed' && !appt.installation_appointment_id ? (
                          <button type="button" className="update-button" disabled={actionBusy} onClick={openScheduleModal}>
                            Set Installation Appointment
                          </button>
                        ) : null}
                        <button type="button" className="cancel-button" disabled={actionBusy} onClick={() => void handleReopen()}>
                          Reopen
                        </button>
                      </>
                    )}
                  </div>
                </div>

                <JobCodeSection jobCode={appt.job_code} clientId={appt.client_id} />

                <div className="si-info-card">
                  <div className="si-info-item">
                    <span className="si-info-label">Business Address</span>
                    <span className="si-info-value">{appt.business_address || '—'}</span>
                  </div>
                  <div className="si-info-item">
                    <span className="si-info-label">Point of Contact</span>
                    <span className="si-info-value">{appt.point_of_contact || '—'}</span>
                  </div>
                  <div className="si-info-item">
                    <span className="si-info-label">Contact Phone</span>
                    <span className="si-info-value">{appt.poc_phone || '—'}</span>
                  </div>
                  <div className="si-info-item">
                    <span className="si-info-label">Contact Email</span>
                    <span className="si-info-value">{appt.poc_email || '—'}</span>
                  </div>
                  <div className="si-info-item">
                    <span className="si-info-label">{appt.inspection_end_at ? 'Inspection Window' : 'Inspection Date'}</span>
                    <span className="si-info-value">
                      {appt.inspection_end_at
                        ? `${formatDateTime(appt.inspection_at)} – ${formatDateTime(appt.inspection_end_at)}`
                        : formatDateTime(appt.inspection_at)}
                    </span>
                  </div>
                  <div className="si-info-item">
                    <span className="si-info-label">Inspectors</span>
                    <span className="si-info-value">
                      {appt.assignees.length ? appt.assignees.map((a) => a.username).join(', ') : '—'}
                    </span>
                  </div>
                  {appt.installation_appointment_id ? (
                    <div className="si-info-item">
                      <span className="si-info-label">Installation</span>
                      <span className="si-info-value">
                        <button type="button" className="link-button" onClick={() => navigate(`/installation/${appt.installation_appointment_id}`)}>
                          View installation appointment
                        </button>
                      </span>
                    </div>
                  ) : null}
                </div>

                {appt.status === 'Void' && appt.void_reason ? (
                  <p className="create-order-error">Voided: {appt.void_reason}</p>
                ) : null}

                {readOnly ? (
                  <p className="elevators-confirm-hint">
                    This inspection is {appt.status.toLowerCase()} and is read-only. Reopen it to make changes.
                  </p>
                ) : null}

                {SITE_INSPECTION_SECTIONS.map((section) => (
                  <section className="si-form-section" key={section.id}>
                    <h3 className="si-form-section-title">{section.title}</h3>
                    {section.description ? <p className="si-form-section-desc">{section.description}</p> : null}
                    {section.fields.map((f) => renderField(f))}
                  </section>
                ))}
              </>
            ) : null}
          </div>
        </main>
      </div>

      <Modal isOpen={voidOpen} onClose={() => setVoidOpen(false)} title="Void inspection">
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label" htmlFor="si-void-reason">Reason</label>
            <textarea
              id="si-void-reason"
              className="form-input"
              rows={4}
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              placeholder="Why is this inspection being voided?"
            />
          </div>
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" disabled={actionBusy} onClick={() => setVoidOpen(false)}>Cancel</button>
          <button type="button" className="update-button" disabled={actionBusy || !voidReason.trim()} onClick={() => void handleVoid()}>
            {actionBusy ? 'Voiding…' : 'Void inspection'}
          </button>
        </div>
      </Modal>

      <Modal isOpen={scheduleOpen} onClose={() => setScheduleOpen(false)} title="Schedule installation appointment" wide>
        <div className="modal-body">
          <p className="elevators-confirm-hint" style={{ marginTop: 0 }}>
            The inspection is complete. Schedule the installation appointment for <strong>{appt?.business_name}</strong>.
          </p>
          <div className="form-group">
            <label className="form-label" htmlFor="si-install-at">Date &amp; Time of Installation</label>
            <input
              id="si-install-at"
              type="datetime-local"
              className="form-input"
              value={installAt}
              onChange={(e) => { setInstallAt(e.target.value); setScheduleError(''); }}
              disabled={scheduleBusy}
            />
          </div>
          <div className="form-group">
            <label className="elevators-radio-label">
              <input
                type="checkbox"
                checked={installMultiDay}
                onChange={(e) => {
                  setInstallMultiDay(e.target.checked);
                  if (!e.target.checked) setInstallEndAt('');
                  setScheduleError('');
                }}
                disabled={scheduleBusy}
              />
              This installation spans a date range
            </label>
          </div>
          {installMultiDay ? (
            <div className="form-group">
              <label className="form-label" htmlFor="si-install-end-at">Installation ends</label>
              <input
                id="si-install-end-at"
                type="datetime-local"
                className="form-input"
                value={installEndAt}
                onChange={(e) => { setInstallEndAt(e.target.value); setScheduleError(''); }}
                disabled={scheduleBusy}
              />
            </div>
          ) : null}
          <div className="form-group">
            <span className="form-label">Installer(s)</span>
            <TaskAssigneePicker
              users={assigneePickerUsers}
              value={installAssignees}
              onChange={setInstallAssignees}
              addPlaceholder="Search employee to assign…"
            />
          </div>
          {scheduleError ? <p className="create-order-error" role="alert">{scheduleError}</p> : null}
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" disabled={scheduleBusy} onClick={() => setScheduleOpen(false)}>
            Not now
          </button>
          <button type="button" className="update-button" disabled={scheduleBusy} onClick={() => void submitSchedule()}>
            {scheduleBusy ? 'Scheduling…' : 'Schedule installation'}
          </button>
        </div>
      </Modal>

      <AttachmentVoidModal control={photoVoid} />
    </div>
  );
};

export default SiteInspectionDetail;
